// src/lib/opportunity/formatPresetPricing.ts
//
// Shared helpers for applying manager format presets (calc lines +
// deposit rules) during live quote math.

import type { FormatCalcLineDef } from "./formatCalcLineTypes.js";
import {
  evaluateFormatCalcLines,
  type FormatCalcEvaluation,
} from "./formatCalcEval.js";
import { computeDepositBase } from "./formatDepositEval.js";
import type { FormatDepositRuleDef } from "./formatDepositTypes.js";
import { round2, safeNumber, safePct } from "./calc.js";
import type { OpportunityPricingInputs } from "../../types/opportunity.js";

export type FormatPresetPricingContext = {
  calcLines: FormatCalcLineDef[];
  depositRule: FormatDepositRuleDef;
  baseAmountLabel: string;
};

export function evaluateFormatPreset(args: {
  inputs: OpportunityPricingInputs;
  preset: FormatPresetPricingContext | null | undefined;
}): FormatCalcEvaluation | null {
  const lines = args.preset?.calcLines ?? [];
  if (lines.length === 0) return null;
  return evaluateFormatCalcLines({
    lines,
    baseSubtotal: args.inputs.subtotal_input,
    valuesByLineId: args.inputs.format_calc_values,
    modesByLineId: args.inputs.format_calc_modes,
  });
}

export function subtotalBeforeSalesAdjustments(args: {
  inputs: OpportunityPricingInputs;
  preset: FormatPresetPricingContext | null | undefined;
}): { formatEval: FormatCalcEvaluation | null; amount: number } {
  const formatEval = evaluateFormatPreset(args);
  if (formatEval) {
    return { formatEval, amount: formatEval.totalBeforeTax };
  }
  return { formatEval: null, amount: round2(safeNumber(args.inputs.subtotal_input)) };
}

export function computeGenericDepositAmount(args: {
  inputs: OpportunityPricingInputs;
  totalSalePrice: number;
  formatEval: FormatCalcEvaluation | null;
}): number {
  const total = round2(safeNumber(args.totalSalePrice));
  if (args.inputs.deposit_mode === "amount") {
    return round2(safeNumber(args.inputs.deposit_amount_input));
  }
  const evaluatedLines = args.formatEval
    ? [...args.formatEval.before, ...args.formatEval.after]
    : [];
  if (evaluatedLines.length > 0) {
    const { depositBase } = computeDepositBase({
      saleTotal: total,
      evaluatedLines,
    });
    return round2(depositBase * (safePct(args.inputs.deposit_pct) / 100));
  }
  return round2(total * (safePct(args.inputs.deposit_pct) / 100));
}
